import { Directive, EventEmitter, Injectable, Input, Output } from '@angular/core';
import { formatNumber } from '@emma-helpers/format.helper';
import { ChartOptions, ChartRelativeInput, LayoutContainer, LayoutName } from 'emma-common-ts/emma';
import { debounce } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { EMMAChartLayoutBase } from './emma-chart-layout-base';

@Injectable({
  providedIn: 'root',
})
@Directive()
// tslint:disable-next-line: directive-class-suffix
export class EMMAChartBase extends EMMAChartLayoutBase {
  protected chart: any;
  $renderCompleted = new BehaviorSubject<boolean>(false);

  @Input() layout!: LayoutName;
  @Input() override container!: LayoutContainer;

  @Input() color = 'altgreen';
  @Output() chartLoad = new EventEmitter<boolean>();
  @Output() exported = new EventEmitter<string>();
  exportChart: any;
  protected updateChart: any;

  protected options: Partial<ChartOptions> = {};

  constructor() {
    super();
    this.setup();
    this.exportChart = debounce(() => this._exportChart(), 100);
    this.updateChart = debounce(() => this._updateChart(), 100);
  }
  protected _updateChart(): void {
    return;
  }
  setup() {
    if (this.totalValue !== undefined) {
      this.updateTotal(this.totalValue);
    }
    if (this.variationValue !== undefined) {
      this.updateVariation(this.variationValue);
    }
  }
  init() {
    this.setup();
  }
  loading() {
    this.isLoading = true;
    this.isError = false;
  }
  loaded() {
    if (this.isLoading) {
      this.isLoading = false;
      this.$renderCompleted.next(true);
    }
  }

  error(error?: unknown) {
    this.isLoading = false;
    this.isError = true;
  }

  updateVariation(variation?: number | string) {
    if (typeof variation === 'string') {
      this.loaded();
      this.variationValue = 0;
      this.variation = variation;
    } else if (variation !== undefined) {
      this.loaded();
      this.variationValue = variation;
      this.variation = !isNaN(variation)
        ? formatNumber(variation, {
            style: 'percent',
            maximumFractionDigits: 2,
          })
        : '';
    } else {
      this.variation = '';
    }
  }
  updateTotal(total?: number | string) {
    if (typeof total === 'string') {
      this.loaded();
      this.totalValue = 0;
      this.total = total;
    } else if (total !== undefined) {
      this.loaded();
      this.totalValue = total;
      this.total = !isNaN(total) ? formatNumber(total, this.totalOptions) : '';
    }
  }
  updateRelative(
    data: {
      total: number | string;
      variation: number | string | null;
    } | null
  ) {
    if (!data) {
      this.loading();
    } else {
      const { total, variation } = data;
      this.updateTotal(total);
      this.updateVariation(variation ?? '-');
    }
  }
  update(data: any, options?: any) {
    if (!data || data.loading) {
      this.loading();
    } else if (data.error) {
      this.isError = Boolean(data.error);
    } else {
      this.updateRelative(data);
    }
  }

  addLegend(legend: ChartRelativeInput) {
    const legends = this.legends.slice();
    legends[legend.index] = { ...legend };
    this.legends = legends;
  }

  private _exportChart() {
    if (this.chart) {
      this.$renderCompleted
        .pipe(
          filter((val) => val),
          takeUntil(this.exported)
        )
        .subscribe(() => {
          if (this.chart.export) {
            // AmCharts
            const toPNGCallback = (data: any) => {
              this.exported.emit(data);
            };
            this.chart.export.capture({}, function (this: any) {
              this.toPNG({}, toPNGCallback);
            });
          } else if (this.chart.toBase64Image) {
            // Chart.js
            this.exported.emit(this.chart.toBase64Image());
          } else {
            console.error('Unknow chart type to export', this.chartId, this.chart);
          }
        });
    } else if (this.total) {
      this.exported.emit(this.total);
    }
  }
}
